import { t } from "../../../libs/i18n";
import { formatNumber } from "../../../libs/utils";
import type { FormBalance } from "../use-pay-form";
import { compareBalanceItemPriority } from "./balance-sort-utils";

type CalculateBalancesParams = {
  balances: FormBalance[];
  fundSourceId: string;
  newAmount: number;
  payAmount: number | undefined;
};

type CalculateBalancesResult = {
  updatedBalances: FormBalance[];
  valid: boolean;
  errorMessages: string[];
  newPayAmount?: number;
};

/**
 * ポイントの利用優先度に基づいてbalancesを並び替える
 * 1. 有効期限が近い
 * 2. 利用可能店舗の個別選択数が少ない
 * 3. 特定地域のみ利用可の設定数が少ない
 * 4. ポイントの残高が少ない
 * 5. priorityWeightが高い
 */
function sortBalancesByPriority(balances: FormBalance[]): FormBalance[] {
  return balances
    .filter((balance) => balance.isAvailable !== false)
    .sort(compareBalanceItemPriority);
}

export const formatMismatchMessage = (
  total: number,
  expected: number,
): string => {
  const diff = total - expected;
  return t("ポイントの合計が支払い額と一致しません（{diff}pt）", {
    diff: diff > 0 ? `+${formatNumber(diff)}` : formatNumber(diff),
  });
};

const countSelectedFundSources = (balances: FormBalance[]): number => {
  const selectedFundSources = new Set<string>();

  for (const balance of balances) {
    if (Number(balance.balanceState.amount) > 0 && balance.fundSourceId) {
      selectedFundSources.add(balance.fundSourceId);
    }
  }

  return selectedFundSources.size;
};

/**
 * 入力された残高の金額に基づいて、balances、valid、errorMessagesを計算する
 */
export function calculateBalances({
  balances,
  fundSourceId,
  newAmount,
  payAmount,
}: CalculateBalancesParams): CalculateBalancesResult {
  let valid = true;
  const errorMessages: string[] = [];
  let newPayAmount = payAmount;

  // payAmountがない場合、エラーメッセージを設定
  if (payAmount === undefined) {
    valid = false;
    errorMessages.push(t("支払金額を入力してください"));
    newPayAmount = newAmount;
  }

  // newAmountが対象のbalanceのuserBalance.amountを超えているかチェック
  const targetBalance = balances.find((b) => b.fundSourceId === fundSourceId);
  if (targetBalance && targetBalance.isAvailable === false) {
    return {
      updatedBalances: balances,
      valid: false,
      errorMessages: [
        targetBalance.unavailableReason ?? t("この店舗では利用できません"),
      ],
      newPayAmount,
    };
  }

  const availableAmount = targetBalance
    ? Number(targetBalance.userBalance?.amount) || 0
    : 0;

  const isExceedingBalance = newAmount > availableAmount;
  if (isExceedingBalance) {
    valid = false;
    errorMessages.push(t("残高を超えるポイントは入力できません"));
  }

  // payAmountがあり、newAmountがpayAmountより多い場合
  if (payAmount !== undefined && newAmount > payAmount) {
    valid = false;
    errorMessages.push(t("内訳の合計が支払い額を超えています"));
  }

  // 指定されたfundSourceIdの残高を更新
  const updatedBalances = balances.map((balance) => {
    if (balance.fundSourceId === fundSourceId) {
      return {
        ...balance,
        balanceState: {
          ...balance.balanceState,
          amount: newAmount,
          valid: !isExceedingBalance,
          errorMessage: isExceedingBalance
            ? t("残高を超えるポイントは入力できません")
            : null,
        },
      };
    }
    return balance;
  });

  const selectedFundSourceCount = countSelectedFundSources(updatedBalances);
  const previousSelectedFundSourceCount = countSelectedFundSources(balances);

  // payAmountが存在する場合かつnewAmountがpayAmount以下の場合、他の残高を調整
  // ポイント種類が3種類以上の場合は、副作用を避けるため、手動入力した内訳を維持する
  if (
    payAmount !== undefined &&
    newAmount <= payAmount &&
    selectedFundSourceCount <= 2 &&
    previousSelectedFundSourceCount <= 2
  ) {
    // 残りの金額を計算
    let remainingAmount = payAmount - newAmount;

    // 優先度順に並べ替え
    const sortedBalances = sortBalancesByPriority(
      updatedBalances.filter((b) => b.fundSourceId !== fundSourceId),
    );

    // 一旦すべてのバランス（変更済みのものを除く）をゼロに設定
    const intermediateBalances = updatedBalances.map((balance) => {
      if (balance.fundSourceId === fundSourceId) {
        return balance;
      }
      return {
        ...balance,
        balanceState: {
          ...balance.balanceState,
          amount: 0,
          valid: true,
          errorMessage: null,
        },
      };
    });

    // 優先度順に金額を割り当てる
    const finalBalances = [...intermediateBalances];
    for (const sortedBalance of sortedBalances) {
      if (remainingAmount <= 0) break;

      const balanceIndex = finalBalances.findIndex(
        (b) => b.fundSourceId === sortedBalance.fundSourceId,
      );
      if (balanceIndex === -1) continue;

      const availableAmount = Number(sortedBalance.userBalance?.amount) || 0;
      if (availableAmount <= 0) continue;

      // このバランスから使用する金額を計算
      const amountToUse = Math.min(remainingAmount, availableAmount);
      remainingAmount -= amountToUse;

      finalBalances[balanceIndex] = {
        ...finalBalances[balanceIndex],
        balanceState: {
          ...finalBalances[balanceIndex].balanceState,
          amount: amountToUse,
          valid: true,
          errorMessage: null,
        },
      };
    }

    // 全ての残高の合計が総額と一致するか検証
    const sumAmount = finalBalances.reduce(
      (sum, b) => sum + b.balanceState.amount,
      0,
    );

    const adjustmentValid = sumAmount === payAmount;
    if (!adjustmentValid) {
      valid = false;
      errorMessages.push(formatMismatchMessage(sumAmount, payAmount));
    }

    return {
      updatedBalances: finalBalances,
      valid: valid && adjustmentValid,
      errorMessages,
      newPayAmount,
    };
  }

  return {
    updatedBalances,
    valid,
    errorMessages,
    newPayAmount,
  };
}

/**
 * ポイントの利用優先度に基づいて、支払金額をバランスに分配する
 * @param balances バランスのリスト
 * @param payAmount 支払金額
 * @param validate バリデーション関数
 * @returns 更新されたバランス、有効性、エラーメッセージ
 */
export function distributeBalances({
  balances,
  payAmount,
  validate,
}: {
  balances: FormBalance[];
  payAmount: number;
  validate: (amount: number) => [boolean, string | null];
}): {
  updatedBalances: FormBalance[];
  valid: boolean;
  errorMessages: string[];
} {
  // 支払金額のバリデーション
  const [valid, errorMessage] = validate(payAmount);
  if (!valid) {
    return {
      updatedBalances: balances.map((balance) => ({
        ...balance,
        balanceState: {
          ...balance.balanceState,
          amount: 0,
          valid: false,
          errorMessage: null,
        },
      })),
      valid,
      errorMessages: errorMessage ? [errorMessage] : [],
    };
  }

  // 優先度に基づいてバランスを並び替え
  const sortedBalances = sortBalancesByPriority(balances);

  // 一旦すべてのバランスをゼロに設定
  const intermediateBalances = balances.map((balance) => ({
    ...balance,
    balanceState: {
      ...balance.balanceState,
      amount: 0,
      valid: true,
      errorMessage: null,
    },
  }));

  // 残りの金額
  let remainingAmount = payAmount;

  // 優先度順に金額を割り当てる
  const finalBalances = [...intermediateBalances];
  for (const sortedBalance of sortedBalances) {
    if (remainingAmount <= 0) break;

    const balanceIndex = finalBalances.findIndex(
      (b) => b.fundSourceId === sortedBalance.fundSourceId,
    );
    if (balanceIndex === -1) continue;

    const availableAmount = Number(sortedBalance.userBalance?.amount) || 0;
    if (availableAmount <= 0) continue;

    // このバランスから使用する金額を計算
    const amountToUse = Math.min(remainingAmount, availableAmount);
    remainingAmount -= amountToUse;

    finalBalances[balanceIndex] = {
      ...finalBalances[balanceIndex],
      balanceState: {
        ...finalBalances[balanceIndex].balanceState,
        amount: amountToUse,
        valid: true,
        errorMessage: null,
      },
    };
  }

  const totalAmount = finalBalances.reduce(
    (sum, b) => sum + b.balanceState.amount,
    0,
  );

  if (payAmount > totalAmount) {
    return {
      updatedBalances: finalBalances,
      valid: false,
      errorMessages: [t("残高が不足しています")],
    };
  }

  // 全てのバランスの合計が総額と一致するか検証
  const sumAmount = finalBalances.reduce(
    (sum, b) => sum + b.balanceState.amount,
    0,
  );

  const adjustmentValid = sumAmount === payAmount;
  if (!adjustmentValid) {
    return {
      updatedBalances: finalBalances,
      valid: false,
      errorMessages: [formatMismatchMessage(sumAmount, payAmount)],
    };
  }

  return {
    updatedBalances: finalBalances,
    valid: true,
    errorMessages: [],
  };
}
